import React, { useState, useEffect } from "react";
import { Popover, PopoverTrigger, PopoverContent, Chip, Button, Input, Divider, Checkbox, Tooltip, useDisclosure, Spinner } from "@heroui/react";
import { AddIcon, CloseIcon } from '../../utils/svgIcons';
import { CiViewColumn } from "react-icons/ci";
import { IoSaveOutline } from "react-icons/io5";
import ModalBox from '../../components/PopupModals/Modal';
import toast, { Toaster } from 'react-hot-toast';
import { useAuth } from "../../hooks/useAuth";
import { useDispatch } from "react-redux";
import { useSavedColumns } from '../../utils/useSavedColumns';

const DynamicPopover = ({ buttonText, isLoading, leadType, items, selectedColumns, toggleItem, setSelectedColumns, placeholder = "Search here...", popoverTitle = "Select Items", savePreference, fetchPreferences, deletePreference }) => {
  const dispatch = useDispatch();
  const { savedColumns, status, user } = useAuth();
  const { getSavedColumns } = useSavedColumns(leadType, selectedColumns);

  const [searchTerm, setSearchTerm] = useState("");
  const [filteredItems, setFilteredItems] = useState(items);
  const [isOpen, setIsOpen] = useState(false);
  const [newColumnSet, setNewColumnSet] = useState([])
  const [showConfirmSave, setShowConfirmSave] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  // const [savedColumns, setSavedColumns] = useState([]);

  const { onClose } = useDisclosure();

  // Update filtered items when search term changes
  useEffect(() => {
    setFilteredItems(
      items
        .filter(
          (item) =>
            !["id", "_id", "flag", "actions"].includes(item.uid.toLowerCase()) // Exclude unwanted fields
        )
        .filter((item) =>
          item.name.toLowerCase().includes(searchTerm.toLowerCase()) // Apply search
        )
    );
  }, [searchTerm, items]);


  useEffect(() => {
    if (isOpen) {
      dispatch(fetchPreferences(user?._id))
    }
    // const storedFilters = JSON.parse(localStorage.getItem('savedColumns')) || {};
    // setSavedColumns(storedFilters);
  }, [isOpen]);

  // Check if all items are selected
  const isAllSelected = selectedColumns.length === items.length;

  // Toggle "Select All"
  const toggleSelectAll = () => {
    if (isAllSelected) {
      setSelectedColumns([]); // Deselect all
    } else {
      setSelectedColumns(items.map((item) => item.uid)); // Select all
    }
  };

  const handleSaveColumns = () => {
    setIsOpen(false);
    onClose();
    setShowConfirmSave(true)
  }

  const confirmSaveColumn = () => {
    setShowSaveModal(true);
    setIsOpen(false);
  };

  // Handle Save Columnset
  const saveColumnSet = () => {
    if (!newColumnSet.trim()) return;

    // const storedColumns = JSON.parse(localStorage.getItem('savedColumns')) || {};
    // if (!storedColumns[leadType]) {
    //   storedColumns[leadType] = [];
    // }

    const isDuplicate = savedColumns[leadType]?.some(f => f.name === newColumnSet);
    if (isDuplicate) {
      toast.error("A column with this name already exists!");
      return;
    }

    const saveColumnData = {
      adminId: user?._id,
      leadType,
      type: 'column',
      name: newColumnSet,
      data: [...selectedColumns]
    }

    dispatch(savePreference(saveColumnData)).then((res) => {
      setShowConfirmSave(false);
      toast.success(res?.payload?.message);
      setTimeout(() => {
        setNewColumnSet("");
        setShowSaveModal(false);
      }, 500);
      setIsOpen(true);
      dispatch(fetchPreferences(user?._id))
    }).catch((err) => {
      console.error('Error:', err);
    })

    // const newColumnset = { name: newColumnSet, columns: [...selectedColumns] };
    // storedColumns[leadType].push(newColumnset);

    // setSavedColumns(storedColumns);
    // localStorage.setItem('savedColumns', JSON.stringify(storedColumns));

    // toast.success('Selected columns saved successfully');
  };

  const removeSavedColumn = (columnIndex) => {
    const saveColumnsData = {
      adminId: user?._id,
      leadType,
      type: 'column',
      index: columnIndex,
      name: savedColumns[leadType][columnIndex].name
    }

    dispatch(deletePreference(saveColumnsData)).then((res) => {
      toast.success(res?.payload?.message);
      setTimeout(() => {
        dispatch(fetchPreferences(user?._id))
      }, 500);
    }).catch((err) => {
      console.error('Error:', err);
      toast.error(err.message);
    })

    // setSavedColumns((prevFilters) => {
    //   if (!prevFilters[leadType]) return prevFilters;

    //   // Remove the filter from state
    //   const updatedColumn = {
    //     ...prevFilters,
    //     [leadType]: prevFilters[leadType].filter((_, index) => index !== columnIndex),
    //   };

    //   if (updatedColumn[leadType].length === 0) {
    //     delete updatedColumn[leadType];
    //   }

    //   // Save updated filters to localStorage
    //   localStorage.setItem("savedColumns", JSON.stringify(updatedColumn));

    //   return updatedColumn;
    // });
    // toast.success('Column removed successfully');
  };

  const applySavedColumns = (savedColumns) => {
    setSelectedColumns(savedColumns?.columns);
  };

  useEffect(() => {
    const saved = getSavedColumns();
  }, [leadType]);

  return (
    <>
      <Popover placement="bottom-start" onOpenChange={(open) => setIsOpen(open)}>
        <PopoverTrigger>
          <Button
            className={`font-medium border-1 border-gray-200 text-medium text-gray-500 transition-colors duration-500 ${isOpen ? "bg-button text-white border-secondary" : "hover:bg-button hover:border-secondary hover:text-white"
              }`}
            variant="bordered" startContent={<CiViewColumn size={33} />}>
            <p className="hidden 2xl:block">{buttonText}</p>
          </Button>
        </PopoverTrigger>
        {isOpen && (
          <PopoverContent className="bg-white w-[350px] p-0">
            {/* Search Input */}
            <div className="px-5 py-5 w-full flex justify-center items-center gap-4">
              <Input
                classNames={{
                  base: "max-w-full h-10",
                  inputWrapper: "h-full font-normal text-default-500 border-solid rounded-3xl border-gray-300 px-4",
                }}
                placeholder={placeholder}
                size="sm"
                type="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <Divider />

            {status?.fetch === 'loading' ? (
              <div className="saved-filters px-6 py-4 w-full border-b border-default-200 flex gap-2 whitespace-nowrap overflow-auto default-scrollbar">
                <Spinner color="success" size="sm" />
                <p className="!text-success text-[18px]">Loading...</p>
              </div>
            ) : (
              <>
                {savedColumns[leadType] && savedColumns[leadType].length > 0 && (
                  <div className="saved-filters px-6 py-4 w-full border-b border-default-200 flex gap-2 whitespace-nowrap overflow-auto default-scrollbar">
                    {savedColumns[leadType].map((column, index) => (
                      <Tooltip
                        key={column.name}
                        showArrow
                        classNames={{
                          base: ["before:bg-neutral-400 dark:before:bg-success"],
                          content: ["py-2 px-4 ", "text-red font-medium from-white to-neutral-400"],
                        }}
                        content={`Saved Column : ${column.name}`}
                        placement="bottom"
                      >
                        <Chip key={index} className="bg-success text-white cursor-pointer" >
                          <div className="flex items-center gap-2">
                            <span onClick={() => applySavedColumns(column)}>{column.name}</span>
                            <span
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent clicking on the chip itself
                                removeSavedColumn(index);
                              }}
                            ><CloseIcon width="15px" height="15px" className="!text-secondary" /></span>
                          </div>
                        </Chip>
                      </Tooltip>

                    ))}
                  </div>
                )}
              </>
            )}


            {/* Item Selection List */}
            <div className="w-full px-5 py-5 flex flex-col h-[350px] overflow-auto default-scrollbar">
              {isLoading ? (
                <Button className="w-full text-large p-10 text-black" isLoading color="blue">
                  Fetching columns...
                </Button>
              ) :
                <>
                  <div className="flex w-full !justify-between !items-center gap-2 pb-3 px-2">
                    <h4 className="text-xl font-semibold ">{popoverTitle}</h4>
                    <div>
                      <Tooltip
                        key={"saveColumns"}
                        showArrow
                        classNames={{
                          base: ["before:bg-neutral-400 dark:before:bg-success"],
                          content: ["py-2 px-4 ", "text-red font-medium from-white to-neutral-400"],
                        }}
                        content={'Save Columns Set.'}
                        placement="top"
                      >
                        <div><IoSaveOutline onClick={handleSaveColumns} className='cursor-pointer text-2xl' /></div>
                      </Tooltip>
                    </div>
                  </div>
                  <div className="selectitem flex flex-col gap-4 px-2">
                    {/* Select All Option */}
                    {items.length > 0 && (
                      <Checkbox
                        classNames={{ icon: "text-white" }}
                        color="success"
                        isSelected={isAllSelected}
                        onChange={toggleSelectAll}
                      >
                        Select All
                      </Checkbox>
                    )}

                    {/* Individual Items */}
                    {filteredItems.length > 0 ? (
                      filteredItems.map((item) => (
                        <Checkbox
                          classNames={{ icon: "text-white" }}
                          color="success"
                          key={item.uid}
                          isSelected={Object.values(selectedColumns)?.includes(item.uid)}
                          onChange={() => toggleItem(item.uid)}
                        >
                          {item.name}
                        </Checkbox>
                      ))
                    ) : (
                      <p className="text-gray-500 px-2">No matching items found.</p>
                    )}
                  </div>
                </>
              }
            </div>
          </PopoverContent>
        )}
      </Popover>
      {showConfirmSave && (
        <ModalBox
          title={'Want to save selected columns?'}
          isOpen={showConfirmSave}
          onOpen={() => setShowConfirmSave(true)}
          onClose={() => {
            setShowConfirmSave(false);
            setIsOpen(true);
          }}
          handleConfirmUpdate={confirmSaveColumn}
        >
          <div className="flex flex-col gap-[5px]">
            <p className='text-amber-600 font-semibold'>Are you sure? You want to save this column set.</p>
            <p className="flex gap-1"><span className='font-bold'>Columns</span> : <span className='text-success'>{selectedColumns.join(", ")}</span></p>
          </div>
        </ModalBox>
      )}
      {showSaveModal && (
        <ModalBox
          title={'Save Column Set'}
          isOpen={showSaveModal}
          onOpen={() => setShowSaveModal(true)}
          onClose={() => setShowSaveModal(false)}
          handleConfirmUpdate={saveColumnSet}
          isLoading={status?.save === 'loading' ? true : false}
        >
          <div className="flex flex-col gap-[10px]">
            <Input type="text" placeholder="Enter column set name..." value={newColumnSet} onChange={(e) => setNewColumnSet(e.target.value)} />
          </div>
        </ModalBox>
      )}
      <Toaster
        position="top-right"
        reverseOrder={false}
      />
    </>
  );
};

export default DynamicPopover;